// Copyright 2026 Linka Cloud  All rights reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { LockOutlined, VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import { Button, IconButton, InputAdornment, Stack, TextField } from '@mui/material';
import React, { useState } from 'react';
import { useIsSubmitting } from '../hooks';

export interface PasswordFormProps {
  submitLabel?: string;
  postUrl?: string;
  csrf?: string;
}

export const PasswordForm = ({ submitLabel, postUrl, csrf }: PasswordFormProps) => {
  const [isSubmitting, handleSubmit] = useIsSubmitting();
  const [password, _setPassword] = useState('');
  const [passwordConfirm, _setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [error, setError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const setPassword = (value: string) => {
    if (error && value.length >= 8) {
      setError('');
    }
    if (value === passwordConfirm) {
      setConfirmError('');
    }
    if (passwordConfirm !== '' && value !== passwordConfirm) {
      setConfirmError('Passwords do not match');
    }
    _setPassword(value);
  };
  const setPasswordConfirm = (value: string) => {
    if (confirmError && value === password) {
      setConfirmError('');
    }
    _setPasswordConfirm(value);
  };
  return (
    <Stack component="form" method="post" action={postUrl} onSubmit={handleSubmit}>
      <Stack>
        <TextField
          id="password"
          name="password"
          label="Password"
          placeholder="••••••••"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onBlur={() => setError(password.length < 8 ? 'Password must be at least 8 characters' : '')}
          error={!!error}
          helperText={error}
          slotProps={{
            htmlInput: {
              minLength: 8,
            },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlined />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="toggle password visibility" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          id="password-confirm"
          name="passwordConfirm"
          label="Confirm Password"
          placeholder="••••••••"
          type={showPasswordConfirm ? 'text' : 'password'}
          value={passwordConfirm}
          onChange={(e) => setPasswordConfirm(e.target.value)}
          onBlur={() => setConfirmError(passwordConfirm !== password ? 'Passwords do not match' : '')}
          error={!!confirmError}
          helperText={confirmError}
          slotProps={{
            htmlInput: {
              minLength: 8,
            },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlined />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                  >
                    {showPasswordConfirm ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </Stack>
      <Stack direction="column">
        <Stack direction="row" spacing={4}>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={isSubmitting || password === '' || password !== passwordConfirm || error != ''}
          >
            {submitLabel || 'Change Password'}
          </Button>
        </Stack>
      </Stack>
      <input hidden readOnly name="csrf" value={csrf} />
    </Stack>
  );
};
